import { useState, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'

import Rollbar from 'utils/rollbar'
import { isAuth0Timeout, isRoleAllowed, saveCustomerIdFromCustomerAuth } from 'utils/auth'
import {
  fetchCurrentUser,
  saveAuthToken,
  terminateSession,
  revokeFactSetIdpSession,
} from 'services/auth'
import { factset as factsetConfig } from 'config'

import { ForbiddenError } from 'constants/errors'
import { AUTH_ERROR_TYPES } from 'constants/errorCodes'
import { CUSTOMER_ID, NEW_AUTH_KEY } from 'constants/localStorage'
import {
  JUST_LOGGED_OUT_KEY,
  JUST_SESSION_EXPIRED_KEY,
  NEEDS_FACTSET_LOGIN_KEY,
} from 'constants/sessionStorage'

const TOKEN_ERRORS = ['login_required', 'consent_required']

// Reads a sessionStorage flag exactly once, clears it if present, and AND-gates the result with
// NEW_AUTH_KEY. Used as a lazy useState initializer so each flag is consumed on mount and frozen
// for the hook's lifetime — re-renders (e.g. when unleash flags resolve) cannot flip it back.
function readAndClearSessionFlag(key) {
  const wasSet = Boolean(sessionStorage.getItem(key))
  if (wasSet) {
    sessionStorage.removeItem(key)
  }
  return wasSet && Boolean(localStorage.getItem(NEW_AUTH_KEY))
}

// This token is only set up to minimize the impact of having an account
// that has not been properly configured and has a valid authenticated_id present,
// in this case the client cannot determine that the user is invalid (since it receives a 401)
// and will attempt to reauthenticate against the tenant. Since the session is valid
// on auth0 the flow will continue to the API, where the invalid user will once again
// fail to be authenticated. To avoid this infinite redirect loop, we set a control flag and report
// that a problem was encountered.
const AUTH0_AUTH_ATTEMPT_TOKEN = 'auth0-authentication-attempted'
export const auth0AuthAttempted = () => !!localStorage.getItem(AUTH0_AUTH_ATTEMPT_TOKEN)
export const registerAuth0AuthAttempt = () => localStorage.setItem(AUTH0_AUTH_ATTEMPT_TOKEN, true)

function useAuth() {
  const [currentUser, setCurrentUser] = useState(null)

  // These must be lazy useState initializers, NOT values recomputed on each render. Each one
  // runs its cleanup exactly once on mount and freezes the flag for the hook's lifetime. If we
  // recomputed inline, a re-render would read the flag as gone, flip it to false, and let
  // fetchAuth0User silently re-auth the user against PingFederate's persistent session —
  // exactly what these guards prevent.
  const [isSignedOut] = useState(() => readAndClearSessionFlag(JUST_LOGGED_OUT_KEY))
  const [isSessionExpired] = useState(() => readAndClearSessionFlag(JUST_SESSION_EXPIRED_KEY))
  // Set when getAccessTokenSilently fails for FactSet users (initial login or page refresh after
  // logout). Shows the interstitial without a logout/expiry message so the user explicitly signs in.
  const [needsFactsetLogin] = useState(() => readAndClearSessionFlag(NEEDS_FACTSET_LOGIN_KEY))

  const { isLoading, getAccessTokenSilently, loginWithRedirect, logout } = useAuth0()

  useEffect(() => {
    async function fetchAuth0User() {
      try {
        if (auth0AuthAttempted()) {
          return
        }

        const token = await getAccessTokenSilently()
        saveAuthToken(token)
        await fetchUser()
      } catch (error) {
        if (TOKEN_ERRORS.includes(error?.error)) {
          if (localStorage.getItem(NEW_AUTH_KEY)) {
            // For FactSet users we can't tell whether the token error came from Auth0 or
            // PingFederate, so show the interstitial to let the user explicitly sign in.
            sessionStorage.setItem(NEEDS_FACTSET_LOGIN_KEY, 'true')
            window.location.href = window.location.origin
          } else {
            loginWithRedirect({
              appState: {
                returnTo: window.location.pathname,
              },
              redirectUri: window.location.origin,
            })
          }
          return
        }

        // ignore timeout errors to avoid excesive noise if the refresh fails,
        // in most cases this is a false positive due to the auth token being valid
        // for a day
        if (isAuth0Timeout(error)) {
          return
        }

        setCurrentUser(() => {
          throw error
        })
      }
    }

    async function fetchUser() {
      try {
        // we want to clear any customer id previously saved in localStorage before fetching current user again
        // this is to ensure we are always fetching the user with default customer within Admin app
        localStorage.removeItem(CUSTOMER_ID)
        const user = await fetchCurrentUser()

        if (!isRoleAllowed(user)) {
          throw new ForbiddenError({ user })
        }

        saveCustomerIdFromCustomerAuth(user.customerAuthorizations)
        Rollbar.configure({ payload: { person: { id: user.id, email: user.email } } })

        setCurrentUser(user)
      } catch (error) {
        if (error?.response?.data?.errors?.some(e => e.code === AUTH_ERROR_TYPES.identityLocked)) {
          throw error
        }

        if (error?.response?.status === 401) {
          registerAuth0AuthAttempt()
        }

        throw error
      }
    }

    if (!isSignedOut && !isSessionExpired && !needsFactsetLogin) {
      fetchAuth0User()
    }
  }, [getAccessTokenSilently, loginWithRedirect, isSignedOut, isSessionExpired, needsFactsetLogin])

  async function endSession() {
    try {
      await terminateSession()
    } catch {
      // ignore failures terminating the session, this is done optimistically
      // to invalidate the token on our end and issue a new one during the next login
    } finally {
      // Only set the signed-out interstitial flag and clear the FactSet IdP session when
      // the FactSet auth FF is active (indicated by NEW_AUTH_KEY). Both are no-ops for
      // users who have never authenticated via FactSet.
      if (localStorage.getItem(NEW_AUTH_KEY)) {
        sessionStorage.setItem(JUST_LOGGED_OUT_KEY, 'true')

        try {
          await revokeFactSetIdpSession(factsetConfig.authLogoutUrl)
        } catch {
          // ignore — proceed with Auth0 logout regardless
        }
      }

      logout({ returnTo: window.location.origin })
    }
  }

  /**
   * Triggers an Auth0 login redirect, routing through FactSet PingFederate when NEW_AUTH_KEY is set.
   * @returns {void}
   */
  function signIn() {
    loginWithRedirect({
      appState: { returnTo: '/' },
      redirectUri: window.location.origin,
      ...(localStorage.getItem(NEW_AUTH_KEY) && { connection: 'factset' }),
    })
  }

  async function refetchCurrentUser() {
    const user = await fetchCurrentUser()
    setCurrentUser(user)
  }

  return {
    state: { isLoading, currentUser, isSignedOut, isSessionExpired, needsFactsetLogin },
    handlers: { endSession, refetchCurrentUser, signIn },
  }
}

export default useAuth
